import { ArrowRight, Download } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import PageStrip from '@/components/layout/PageStrip';
import Spinner from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import DescriptionListItem from '@/features/Quote/components/sidebar/DescriptionListItem';
import { useAppStatePolling } from '@/hooks/useAppStatePolling';
import { getStatusLabel } from '@/lib/quoteStatus';
import AbsenceSection from './RequestOverview/components/AbsenceSection';
import EmployeesSection from './RequestOverview/components/EmployeesSection';
import EndQuestions from './RequestOverview/components/EndQuestions';
import Field from './RequestOverview/components/Field';
import PackageOverview from './RequestOverview/components/PackageOverview';
import QuotesOverview from './RequestOverview/components/QuotesOverview';
import SectionCard from './RequestOverview/components/SectionCard';
import usePolicyDocumentDownload from './RequestOverview/hooks/usePolicyDocumentDownload';
import useRequestQuotes from './RequestOverview/hooks/useRequestQuotes';

const RequestOverview = (): React.JSX.Element | null => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { appData, applicationData, applicationManagement, config } = useAppContext();

	const [isFetchingApplication, setIsFetchingApplication] = useState(!!id);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const fetchApplicationData = applicationManagement.fetch;
	const hasFetchedRef = useRef<string | null>(null);

	useAppStatePolling({ id, enabled: !!id });
	// One fetch, shared by the quote carousels and the package overview.
	const quotes = useRequestQuotes(id);
	const policyDocument = usePolicyDocumentDownload(id);

	const loadApplication = useCallback(() => {
		if (!id) return;
		setIsFetchingApplication(true);
		setFetchError(null);
		fetchApplicationData(id)
			.then(() => {
				hasFetchedRef.current = id;
			})
			.catch((error) => {
				setFetchError(error instanceof Error ? error.message : String(error));
			})
			.finally(() => {
				setIsFetchingApplication(false);
			});
	}, [id, fetchApplicationData]);

	useEffect(() => {
		if (id && hasFetchedRef.current !== id) {
			loadApplication();
		} else {
			setIsFetchingApplication(false);
		}
	}, [id, loadApplication]);

	if (!id) return null;

	// Errors win over the loading guard: a failed fetch leaves appData null, which
	// would otherwise strand the page on the spinner with no way to retry.
	if (fetchError) {
		return (
			<PageStrip className="py-4 lg:py-14" contentClassName="mt-8 sm:mt-16 text-center">
				<p className="mb-4 text-destructive">{t('requestOverview.loadError')}</p>
				<Button onClick={loadApplication}>{t('common.retry')}</Button>
			</PageStrip>
		);
	}

	// appData is loaded by the polling hook; wait for it (or the application fetch).
	if (isFetchingApplication || !appData) {
		return (
			<PageStrip className="py-4 lg:py-14" contentClassName="mt-8 sm:mt-16 flex justify-center">
				<Spinner size="lg" visible />
			</PageStrip>
		);
	}

	const notAvailable = t('requestOverview.notAvailable');
	// Fall back to the raw code so the reader always sees something for an unmapped status.
	const statusLabel = getStatusLabel(t, appData.status) ?? appData.status ?? null;

	const companyInfo = appData.company_info;
	const companyName = companyInfo?.company_name ?? appData.company_name;
	const coc = companyInfo?.coc ?? appData.coc;
	const sectorName = companyInfo?.sector_name ?? appData.sector_name;
	const address = companyInfo?.address ?? appData.address;
	const zipCode = companyInfo?.zip_code ?? appData.zip_code;
	const city = companyInfo?.city ?? appData.city;
	const addressLines = [address, zipCode, city].filter((val): val is string => Boolean(val));

	// Current-policy fields, kept identical to what the regular flow's sidebar shows.
	const currentPolicy = appData.current_policy?.sick_leave_short_term;
	const policyInsurer = currentPolicy?.insurer_name ?? appData.current_policy?.verzekeraar ?? null;
	const policyPremiumRate =
		typeof currentPolicy?.premium_rate_percentage === 'number'
			? currentPolicy.premium_rate_percentage
			: null;

	const contactName =
		[applicationData.initials, applicationData.prefix, applicationData.lastName]
			.filter(Boolean)
			.join(' ')
			.trim() || null;

	return (
		<div className="pb-20 lg:pb-24">
			<PageStrip className="pt-6 sm:pt-8 lg:pt-14 pb-10">
				<header>
					<div className="flex items-start justify-between gap-4">
						<h1 className="min-w-0 text-2xl font-bold leading-tight md:text-3xl">
							{t('requestOverview.title')}
						</h1>
						{statusLabel ? (
							<span
								className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1.5 text-sm text-primary"
								data-testid="dossier-section-status"
							>
								<span className="font-semibold">{t('requestOverview.status.title')}:</span>
								<span className="font-normal">{statusLabel}</span>
							</span>
						) : (
							<span
								className="inline-flex shrink-0 items-center gap-2 text-sm text-muted-foreground/70"
								data-testid="dossier-section-status"
							>
								<span className="font-semibold">{t('requestOverview.status.title')}:</span>
								<span className="font-normal italic">{notAvailable}</span>
							</span>
						)}
					</div>
					<p className="mt-2 text-base md:text-lg text-muted-foreground">
						{t('requestOverview.subtitle')}
					</p>
				</header>
			</PageStrip>

			<PageStrip contentClassName="flex flex-col gap-12">
				{/* Company details (left) + current policy & contact (right) */}
				<div className="grid gap-6 md:grid-cols-2">
					<SectionCard
						title={t('quotes.sidebar.companyInfo')}
						testId="dossier-section-company"
						bodyClassName="pb-2"
					>
						<dl>
							<DescriptionListItem
								title={t('quotes.sidebar.companyName')}
								value={companyName ?? notAvailable}
							/>
							<DescriptionListItem
								title={t('quotes.sidebar.cocNumber')}
								value={coc ?? notAvailable}
							/>
							<DescriptionListItem
								title={t('quotes.sidebar.sector')}
								value={sectorName ?? notAvailable}
							/>
							<DescriptionListItem
								title={t('quotes.sidebar.address')}
								value={addressLines.length > 0 ? addressLines : notAvailable}
							/>
						</dl>
					</SectionCard>

					<div className="flex flex-col gap-12">
						<SectionCard
							title={t('requestOverview.currentPolicyTitle')}
							testId="dossier-section-policy"
							bodyClassName="pb-2"
							action={
								appData.has_current_policy_document ? (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={policyDocument.download}
										loading={policyDocument.isDownloading}
										icon={<Download className="w-4 h-4" />}
										data-testid="dossier-policy-download"
									>
										{t('requestOverview.policyDocument.download')}
									</Button>
								) : null
							}
						>
							<dl className="grid grid-cols-2 gap-x-8">
								<Field label={t('quotes.sidebar.provider')} value={policyInsurer} />
								<Field
									label={t('quotes.sidebar.premiumRate')}
									value={policyPremiumRate != null ? `${policyPremiumRate}%` : null}
								/>
							</dl>
							{policyDocument.error ? (
								<p role="alert" className="mt-2 text-sm text-destructive">
									{policyDocument.error}
								</p>
							) : null}
						</SectionCard>

						<SectionCard
							title={t('requestOverview.contact.title')}
							testId="dossier-section-contact"
						>
							<dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
								<Field label={t('requestOverview.contact.name')} value={contactName} />
								<Field label={t('requestOverview.contact.email')} value={applicationData.email} />
								<Field label={t('requestOverview.contact.phone')} value={applicationData.phone} />
							</dl>
						</SectionCard>
					</div>
				</div>

				{/* Employees */}
				<EmployeesSection appData={appData} />

				{/* Absence figures */}
				<AbsenceSection appData={appData} />

				{/* Quotes per product_type (selected highlighted) + coverage details */}
				<QuotesOverview quotes={quotes} appData={appData} />

				{/* Priced package overview, straight above the end questions */}
				<PackageOverview quotes={quotes} appData={appData} applicationData={applicationData} />

				{/* Answers to the end questions */}
				<EndQuestions applicationData={applicationData} />

				{/* Continue the flow for the client */}
				{!config.read_only_mode && (
					<div className="flex justify-center pt-2">
						<Button
							type="button"
							variant="default"
							size="xl"
							className="font-bold w-full sm:w-auto"
							onClick={() => navigate(`/aanbod/${id}`)}
							iconRight={<ArrowRight className="w-4 h-4" />}
							data-testid="dossier-continue-button"
						>
							{t('requestOverview.continueButton')}
						</Button>
					</div>
				)}
			</PageStrip>
		</div>
	);
};

export default RequestOverview;
